// ============================================================
// AI Rådgiver — Skatt: forslag, konsekvens og anbefaling
// ============================================================
// Kjerneregelen i designet: reduksjon i skattegrunnlag, faktisk skattebesparelse og
// likviditetseffekt vises HVER FOR SEG og summeres aldri. Et fradrag på 19 000 kr er
// ikke 19 000 kr i lomma — det er 19 000 kr lavere skattegrunnlag, som gir noen få
// tusen i spart skatt, og kan samtidig koste 95 000 kr i likviditet.
//
// Hvert forslag bærer derfor fire tall som kan være null hver for seg:
//   taxBaseReduction  — hvor mye skattegrunnlaget faller
//   taxSaving         — hva det er verdt i spart skatt
//   liquidityEffect   — hva det gjør med kontantbeholdningen (fortegnssatt)
//   netCost           — hva det faktisk koster etter skatteeffekten
//
// Alt er estimater. `calculation` returnerer linjene bak tallet, slik at brukeren kan
// etterprøve i stedet for å måtte tro.
const PlummeAiAdvisorTax = (() => {
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);

  // Satser. Speiler functions/assistant/router/handlers/queryTaxEstimateHandler.ts —
  // endres en sats der, endres den her.
  const CORPORATE_TAX_RATES = { NO: 0.22, SE: 0.206, DK: 0.22 };
  // Enkeltpersonforetak: 40 % av resultatet er appens egen bufferregel, ikke en
  // marginalskattesats. Vi bruker den som anslag og sier det i beregningen.
  const ENK_ESTIMATE_RATE = 0.4;
  // Indicative Norwegian AS asset treatment for 2024-2026. Assess each asset
  // separately: direct deduction below NOK 30,000 or under three years; otherwise
  // group a at 30% or group d at 20%. Sources: NO-TAX-FIXED-ASSETS and
  // Skatteetaten's depreciation rates. Unknown facts require review.
  const NO_DIRECT_EXPENSE_LIMIT = 30000;
  const NO_DEPRECIATION_RATE = 0.2;

  const normalizeType = (value) => String(value || '').toUpperCase().replace(/[^A-Z]/g, '');
  const isLimitedCompany = (settings) =>
    ['AS', 'NUF'].includes(normalizeType(settings?.companyTypeCode)) ||
    ['AS', 'NUF'].includes(normalizeType(settings?.companyTypeLabel));

  // Marginalsatsen forslagene regnes med, sammen med hvor den kommer fra. `basis` går
  // videre til beregningsvisningen, så anslaget aldri står uten forklaring.
  const resolveTaxRate = (settings = {}) => {
    const country = settings.country || 'NO';
    if (isLimitedCompany(settings)) {
      return { rate: CORPORATE_TAX_RATES[country] ?? CORPORATE_TAX_RATES.NO, basis: 'corporate', country };
    }
    return { rate: ENK_ESTIMATE_RATE, basis: 'soleTrader', country };
  };

  const round = (value) => Math.round(num(value));

  // ---------- Forslag ----------
  // Et forslag lages bare når tallet bak det finnes. Ingen tomme kort med streker.
  const buildProposals = ({ settings = {}, home, plannedEvents = [], taxRate, year }) => {
    const proposals = [];
    const rate = taxRate.rate;
    const taxBuffer = round(home?.publicFees?.taxBuffer);
    const vatDue = round(home?.publicFees?.vatDue);
    const missingCount = round(home?.followup?.missingAttachmentPurchaseCount ?? home?.followup?.missingAttachmentCount);
    const missingNet = round(home?.followup?.missingAttachmentPurchaseNet ?? home?.followup?.missingAttachmentNet);
    const missingVat = round(home?.followup?.missingAttachmentVat);

    if (taxBuffer > 0) {
      proposals.push({
        id: 'taxReserve',
        icon: 'wallet',
        // Å sette av til skatt reduserer ikke skatten. Det flytter penger du uansett
        // skylder — derfor null i både grunnlag og besparelse.
        taxBaseReduction: null,
        taxSaving: null,
        liquidityEffect: -taxBuffer,
        netCost: null,
        vars: { amount: taxBuffer },
        recommendation: 'do',
        calculation: [{ key: 'taxReserveBase', vars: { amount: taxBuffer, pct: Math.round(ENK_ESTIMATE_RATE * 100) } }],
      });
    }

    if (settings.vatRegistered && vatDue > 0) {
      proposals.push({
        id: 'vatDue',
        icon: 'percent',
        taxBaseReduction: null,
        taxSaving: null,
        liquidityEffect: -vatDue,
        netCost: null,
        vars: { amount: vatDue },
        recommendation: 'do',
        calculation: [{ key: 'vatDueBase', vars: { amount: vatDue } }],
      });
    }

    // Planlagte investeringer før årsslutt. Bare Norge: grensen og saldosatsen under
    // er norske regler, og en svensk eller dansk bruker skal ikke få dem presentert
    // som om de gjaldt der.
    const investments = safeArray(plannedEvents).filter((event) => event.kind === 'investment' && num(event.amount) < 0
      && (!event.date || Number(String(event.date).slice(0, 4)) === year));
    const investmentTotal = investments.reduce((sum, event) => sum + Math.abs(num(event.amount)), 0);
    if (taxRate.country === 'NO' && investmentTotal > 0) {
      const taxCostTotal = investments.reduce((sum, event) => sum + num(event.assetTaxCost), 0);
      const supportedEntity = normalizeType(settings.companyTypeCode) === 'AS' && year >= 2024 && year <= 2026;
      const ready = supportedEntity && investments.every((event) =>
        ['a', 'd'].includes(event.assetGroup) && num(event.assetTaxCost) > 0
        && num(event.assetBusinessUsePct) === 100
        && num(event.assetUsefulLifeYears) > 0 && Number(String(event.assetAcquiredDate || '').slice(0, 4)) === year);
      const reduction = ready ? round(investments.reduce((sum, event) => {
        const cost = num(event.assetTaxCost);
        const direct = cost < NO_DIRECT_EXPENSE_LIMIT || num(event.assetUsefulLifeYears) < 3;
        return sum + cost * (direct ? 1 : event.assetGroup === 'a' ? 0.3 : NO_DEPRECIATION_RATE);
      }, 0)) : null;
      const saving = reduction == null ? null : round(reduction * rate);
      proposals.push({
        id: 'buyAssets',
        icon: 'briefcase',
        taxBaseReduction: reduction,
        taxSaving: saving,
        liquidityEffect: -round(investmentTotal),
        netCost: saving == null || Math.abs(taxCostTotal - investmentTotal) > 1 ? null : round(investmentTotal - saving),
        vars: { amount: round(investmentTotal), count: investments.length },
        reviewRequired: !ready,
        reviewReason: !supportedEntity ? 'unsupportedScope' : !ready ? 'missingFacts' : null,
        // Anbefalingen settes av kalleren, som kjenner likviditetsprognosen.
        recommendation: null,
        calculation: ready ? [
          ...investments.map((event) => ({ key: 'assetPerItem', vars: { label: event.label || '',
            cost: round(num(event.assetTaxCost)), use: num(event.assetBusinessUsePct),
            pct: num(event.assetTaxCost) < NO_DIRECT_EXPENSE_LIMIT || num(event.assetUsefulLifeYears) < 3
              ? 100 : event.assetGroup === 'a' ? 30 : 20 } })),
          { key: 'assetSaving', vars: { reduction, pct: Math.round(rate * 1000) / 10, saving } },
        ] : [{ key: supportedEntity ? 'assetMissingFacts' : 'assetUnsupportedScope', vars: { count: investments.length } }],
      });
    }

    if (missingCount > 0 && missingNet > 0) {
      proposals.push({
        id: 'missingVouchers',
        icon: 'receipt',
        // A missing attachment does not establish that the purchase is deductible.
        reviewAmount: missingNet,
        taxBaseReduction: null,
        taxSaving: null,
        liquidityEffect: 0,
        netCost: null,
        vars: { count: missingCount, amount: missingNet, vat: missingVat },
        recommendation: 'do',
        calculation: [{ key: 'voucherBase', vars: { count: missingCount, amount: missingNet } }],
      });
    }
    return proposals;
  };

  // ---------- «Hvis dette var mitt selskap» ----------
  // Bare forslag der det faktisk finnes et valg tas med. Å betale mva er ikke et valg,
  // og har derfor ingen anbefalingskort.
  const CHOICE_IDS = ['buyAssets', 'taxReserve', 'missingVouchers'];
  const buildChoices = (proposals) => proposals
    .filter((proposal) => CHOICE_IDS.includes(proposal.id))
    .map((proposal) => ({
      id: proposal.id,
      icon: proposal.icon,
      recommendation: proposal.recommendation,
      vars: proposal.vars,
      // Konsekvenskjeden: hver linje er én størrelse, aldri en sum av flere.
      lines: [
        proposal.reviewAmount != null ? { key: 'reviewAmount', value: proposal.reviewAmount, dir: 'flat' } : null,
        proposal.taxSaving != null ? { key: 'tax', value: -proposal.taxSaving, dir: 'down' } : null,
        proposal.liquidityEffect ? { key: 'liquidity', value: proposal.liquidityEffect, dir: proposal.liquidityEffect < 0 ? 'down' : 'up' } : null,
        proposal.netCost != null ? { key: 'netCost', value: proposal.netCost, dir: 'flat' } : null,
      ].filter(Boolean),
    }));

  // Investeringen anbefales utsatt når prognosen ikke tåler uttaket i år. Vi ser bare
  // på månedene som er igjen — en buffer som knakk i mars sier ingenting om desember.
  const recommendForInvestment = ({ forecast, buffer, amount, monthIndex, investmentIncluded = false }) => {
    const remaining = safeArray(forecast).slice(Math.max(0, monthIndex));
    if (!remaining.length || !num(buffer)) return 'consider';
    const lowest = remaining.reduce((min, point) => (num(point.balance) < num(min.balance) ? point : min), remaining[0]);
    return num(lowest.balance) - (investmentIncluded ? 0 : num(amount)) < num(buffer) ? 'wait' : 'do';
  };

  const buildInsights = ({ settings, home, plannedEvents, taxModel, taxPrepayments, year }) => {
    const country = String(settings.country || '').toUpperCase();
    const form = normalizeType(settings.companyTypeCode);
    const supportedAssetEstimate = country === 'NO' && form === 'AS' && year >= 2024 && year <= 2026;
    const vouchers = safeArray(home?.followup?.missingAttachmentItems);
    const investments = safeArray(plannedEvents)
      .filter((event) => event.kind === 'investment' && Number(String(event.date).slice(0, 4)) === year && num(event.amount) < 0)
      .map((event) => ({ id: event.id, label: event.label, purpose: event.purpose, date: event.date,
        amount: round(Math.abs(num(event.amount))), taxDetailsReady: supportedAssetEstimate && ['a', 'd'].includes(event.assetGroup)
          && num(event.assetTaxCost) > 0 && num(event.assetBusinessUsePct) === 100
          && num(event.assetUsefulLifeYears) > 0
          && Number(String(event.assetAcquiredDate || '').slice(0, 4)) === year }));
    const payments = safeArray(taxPrepayments);
    const recordedPaid = payments.reduce((sum, entry) => sum + Math.max(0, num(entry.amount)), 0);
    const settledPaid = num(taxModel?.taxSettlement?.prepaidTotal);
    const paid = round(Math.max(recordedPaid, settledPaid));
    const expected = country === 'NO' && form === 'AS' && !taxModel?.reviewRequired && taxModel?.summary?.taxAmount != null
      ? round(taxModel.summary.taxAmount)
      : country === 'NO' && form === 'ENK' ? round(home?.publicFees?.taxBuffer) : null;
    const basis = form === 'AS' ? 'taxReturn' : form === 'ENK' ? 'enkBuffer' : 'unknown';
    const gap = expected == null || !payments.length && settledPaid <= 0 ? null : Math.max(0, expected - paid);
    return { vouchers, investments, assetEstimateSupported: supportedAssetEstimate,
      advanceTax: { expected, paid, gap, basis } };
  };

  const buildTaxAdvice = ({ settings = {}, home, plannedEvents = [], forecast = [], buffer = 0, monthIndex = 0,
    investmentIncluded = false, taxModel = null, taxPrepayments = [], year = new Date().getFullYear() } = {}) => {
    if (!home) return null;
    const taxRate = resolveTaxRate(settings);
    const proposals = buildProposals({ settings, home, plannedEvents, taxRate, year });
    proposals.forEach((proposal) => {
      if (proposal.id !== 'buyAssets') return;
      proposal.recommendation = recommendForInvestment({
        forecast, buffer, amount: Math.abs(num(proposal.liquidityEffect)), monthIndex, investmentIncluded,
      });
    });
    return { taxRate, proposals, choices: buildChoices(proposals),
      insights: buildInsights({ settings, home, plannedEvents, taxModel, taxPrepayments, year }) };
  };

  return { buildTaxAdvice, buildProposals, buildChoices, resolveTaxRate, recommendForInvestment, buildInsights };
})();

if (typeof globalThis !== 'undefined') globalThis.PlummeAiAdvisorTax = PlummeAiAdvisorTax;
if (typeof module !== 'undefined' && module.exports) module.exports = PlummeAiAdvisorTax;
